import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert, Box, Button, Card, CardContent, Container, Grid, Stack, Step, StepLabel, Stepper,
  Tab, Tabs, Typography,
} from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import LogoutIcon from '@mui/icons-material/Logout';
import StatusBadge, { LOAN_STATUS_MAP, DOCUMENT_STATUS_MAP } from '../../components/shared/StatusBadge';
import SectionHeader from '../../components/shared/SectionHeader';
import EmptyState from '../../components/shared/EmptyState';
import LoadingSkeleton from '../../components/shared/LoadingSkeleton';
import FileUploadModal from '../../components/shared/FileUploadModal';
import DataItem from '../../components/shared/DataItem';
import { useToast } from '../../components/shared/Toast';
import borrowerLoanApi from '../../services/borrowerLoanApi';
import {
  clearBorrowerSession,
  getBorrowerLoanNumber,
  getBorrowerToken,
} from '../../services/borrowerSession';
import { fmtDollar, fmtDate, fmtPhone, fmtPricingPct } from '../../utils/formatters';
import { getAxiosErrorMessage } from '../../utils/getAxiosErrorMessage';

const DEFAULT_MILESTONES = [
  { key: 'application', label: 'Application' },
  { key: 'processing', label: 'Processing' },
  { key: 'underwriting', label: 'Underwriting' },
  { key: 'conditions', label: 'Conditions' },
  { key: 'clear_to_close', label: 'Clear to Close' },
  { key: 'funded', label: 'Funded' },
];

function TabPanel({ value, index, children }) {
  if (value !== index) return null;
  return <Box sx={{ pt: 2 }}>{children}</Box>;
}

function ConditionList({ title, items, onUpload, emptyLabel }) {
  if (!items?.length) {
    return (
      <Box sx={{ mb: 2 }}>
        <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>{title}</Typography>
        <Typography variant="body2" color="text.secondary">{emptyLabel}</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ mb: 2.5 }}>
      <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>{title}</Typography>
      <Stack spacing={1.25}>
        {items.map((item) => (
          <Card key={item.id} variant="outlined">
            <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                justifyContent="space-between"
                alignItems={{ xs: 'stretch', sm: 'center' }}
                spacing={1}
              >
                <Box sx={{ minWidth: 0 }}>
                  <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                    <Typography variant="subtitle2" fontWeight={700}>{item.name}</Typography>
                    <StatusBadge status={item.status} map={DOCUMENT_STATUS_MAP}>
                      {item.status}
                    </StatusBadge>
                  </Stack>
                  {item.description && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                      {item.description}
                    </Typography>
                  )}
                </Box>
                {onUpload && (
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<UploadFileIcon />}
                    onClick={() => onUpload(item)}
                    sx={{ flexShrink: 0 }}
                  >
                    Upload
                  </Button>
                )}
              </Stack>
            </CardContent>
          </Card>
        ))}
      </Stack>
    </Box>
  );
}

const LoanStatusPage = () => {
  const navigate = useNavigate();
  const { showToast, ToastContainer } = useToast();
  const [tab, setTab] = useState(0);
  const [uploadTarget, setUploadTarget] = useState(null);
  const [hasSession] = useState(() => !!getBorrowerToken());

  const {
    data: loan,
    isLoading: loanLoading,
    error: loanError,
    refetch: refetchLoan,
  } = borrowerLoanApi.endpoints.getLoan.useQuery(undefined, { skip: !hasSession });

  const {
    data: conditions,
    isLoading: conditionsLoading,
    refetch: refetchConditions,
  } = borrowerLoanApi.endpoints.getConditions.useQuery(undefined, { skip: !hasSession });

  const [uploadConditionDocument] = borrowerLoanApi.endpoints.uploadConditionDocument.useMutation();

  useEffect(() => {
    if (!hasSession || loanError?.status === 401) {
      clearBorrowerSession();
      navigate('/status', { replace: true });
    }
  }, [hasSession, loanError, navigate]);

  const milestones = useMemo(() => {
    if (loan?.milestones?.length) return loan.milestones;
    return DEFAULT_MILESTONES.map((m, i) => ({
      ...m,
      completed: false,
      current: i === 0,
    }));
  }, [loan]);

  const activeMilestoneIndex = useMemo(() => {
    const currentIdx = milestones.findIndex((m) => m.current);
    if (currentIdx >= 0) return currentIdx;
    const lastCompleted = milestones.map((m) => m.completed).lastIndexOf(true);
    return lastCompleted >= 0 ? Math.min(lastCompleted + 1, milestones.length - 1) : 0;
  }, [milestones]);

  const handleSignOut = () => {
    clearBorrowerSession();
    navigate('/status', { replace: true });
  };

  const handleUpload = async (file) => {
    if (!uploadTarget) return;
    try {
      await uploadConditionDocument({ conditionId: uploadTarget.id, file }).unwrap();
      showToast('Document uploaded successfully', 'success');
      refetchConditions();
      setUploadTarget(null);
    } catch (err) {
      showToast(getAxiosErrorMessage(err, 'Upload failed'), 'error');
      throw err;
    }
  };

  if (!hasSession) return null;

  if (loanLoading) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <LoadingSkeleton />
      </Container>
    );
  }

  if (loanError && loanError.status !== 401) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={() => refetchLoan()}>
              Retry
            </Button>
          }
        >
          Unable to load your loan. {getAxiosErrorMessage(loanError, '')}
        </Alert>
        <Button sx={{ mt: 2 }} onClick={handleSignOut}>
          Back to lookup
        </Button>
      </Container>
    );
  }

  if (!loan) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <EmptyState
          title="Loan not found"
          description="We could not find loan details for this session."
          action={
            <Button variant="contained" onClick={handleSignOut}>
              Try again
            </Button>
          }
        />
      </Container>
    );
  }

  const loanNumber = loan.loanNumber || getBorrowerLoanNumber();
  const borrowerNames = (loan.borrowers || [])
    .map((b) => [b.firstName, b.lastName].filter(Boolean).join(' '))
    .filter(Boolean)
    .join(', ');

  return (
    <Container maxWidth="md" sx={{ py: { xs: 3, md: 4 } }}>
      {ToastContainer}

      <Stack direction="row" justifyContent="flex-end" sx={{ mb: 1 }}>
        <Button size="small" color="inherit" startIcon={<LogoutIcon />} onClick={handleSignOut}>
          Sign out
        </Button>
      </Stack>

      <Card sx={{ mb: 2.5, boxShadow: (t) => t.piper?.shadow?.heroGlow || 'none' }}>
        <CardContent sx={{ p: { xs: 2.5, md: 3 } }}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            justifyContent="space-between"
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            spacing={1.5}
          >
            <Box>
              <Typography variant="caption" color="text.secondary">
                Loan number
              </Typography>
              <Typography variant="h2" component="h1" sx={{ fontFamily: 'monospace' }}>
                {loanNumber}
              </Typography>
              {borrowerNames && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  {borrowerNames}
                </Typography>
              )}
            </Box>
            <Stack alignItems={{ xs: 'flex-start', sm: 'flex-end' }} spacing={0.75}>
              <StatusBadge status={loan.currentStage} map={LOAN_STATUS_MAP}>
                {loan.currentStage}
              </StatusBadge>
              <Typography variant="h3" component="p">
                {fmtDollar(loan.loanAmount)}
              </Typography>
            </Stack>
          </Stack>
        </CardContent>
      </Card>

      <Card sx={{ mb: 2.5 }}>
        <CardContent>
          <SectionHeader title="Loan progress" />
          <Stepper activeStep={activeMilestoneIndex} alternativeLabel sx={{ mt: 1 }}>
            {milestones.map((m) => (
              <Step key={m.key} completed={!!m.completed}>
                <StepLabel>{m.label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        </CardContent>
      </Card>

      <Card>
        <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 1 }}>
          <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable" scrollButtons="auto">
            <Tab label="Overview" />
            <Tab label="Conditions" />
            <Tab label="Details" />
          </Tabs>
        </Box>
        <CardContent>
          <TabPanel value={tab} index={0}>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <DataItem label="Loan type">{loan.loanType || '—'}</DataItem>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <DataItem label="Purpose">{loan.loanPurpose || '—'}</DataItem>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <DataItem label="Product">{loan.productName || '—'}</DataItem>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <DataItem label="Note rate">
                  {loan.noteRate != null ? fmtPricingPct(loan.noteRate) : '—'}
                </DataItem>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <DataItem label="Monthly payment">
                  {loan.monthlyPayment != null ? fmtDollar(loan.monthlyPayment) : '—'}
                </DataItem>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <DataItem label="Property">
                  {[loan.propertyCity, loan.propertyState, loan.propertyZip].filter(Boolean).join(', ') || '—'}
                </DataItem>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <DataItem label="Loan officer">{loan.loName || '—'}</DataItem>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <DataItem label="LO phone">{loan.loPhone ? fmtPhone(loan.loPhone) : '—'}</DataItem>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <DataItem label="LO email">{loan.loEmail || '—'}</DataItem>
              </Grid>
            </Grid>
          </TabPanel>

          <TabPanel value={tab} index={1}>
            {conditionsLoading ? (
              <LoadingSkeleton />
            ) : (
              <>
                <ConditionList
                  title="Action needed"
                  items={conditions?.actionNeeded}
                  onUpload={setUploadTarget}
                  emptyLabel="No conditions need your attention right now."
                />
                <ConditionList
                  title="Under review"
                  items={conditions?.underReview}
                  emptyLabel="No conditions currently under review."
                />
                <ConditionList
                  title="Approved"
                  items={conditions?.approved}
                  emptyLabel="No approved conditions yet."
                />
              </>
            )}
          </TabPanel>

          <TabPanel value={tab} index={2}>
            <SectionHeader title="Key dates" />
            <Grid container spacing={2}>
              {loan.keyDates && Object.keys(loan.keyDates).length > 0 ? (
                Object.entries(loan.keyDates).map(([key, value]) => (
                  <Grid key={key} size={{ xs: 12, sm: 6 }}>
                    <DataItem
                      label={key.replace(/([A-Z])/g, ' $1').replace(/^./, (s) => s.toUpperCase())}
                    >
                      {fmtDate(value)}
                    </DataItem>
                  </Grid>
                ))
              ) : (
                <Grid size={12}>
                  <Typography variant="body2" color="text.secondary">
                    Key dates will appear here as your loan progresses.
                  </Typography>
                </Grid>
              )}
            </Grid>
          </TabPanel>
        </CardContent>
      </Card>

      <FileUploadModal
        show={!!uploadTarget}
        onClose={() => setUploadTarget(null)}
        title={uploadTarget ? `Upload — ${uploadTarget.name}` : 'Upload document'}
        multiple={false}
        onConfirm={handleUpload}
        hint="PDF, PNG, or JPG up to 200 MB"
      />
    </Container>
  );
};

export default LoanStatusPage;
